// Squelette « espace de travail » partagé (Sessions training/live, Challenges,
// Parcours). Refonte 16/07 : onglets HORIZONTAUX en tête (comme Progrès), en-tête
// avec icône-pastille de domaine, plus de sidebar latérale navy. Un seul composant
// aligne toutes les pages consommatrices.
import React, { type ReactNode } from "react";

export interface WorkspaceSection {
  id: string;
  label: string;
  icon: React.ComponentType<{ className?: string }>;
}

/** Carte KPI — style pastille teintée (fond clair + icône couleur de domaine). */
export function StatCard({ icon: Icon, value, label, color }: { icon: React.ComponentType<{ className?: string }>; value: string | number; label: string; color: string }) {
  return (
    <div className="flex items-center gap-3 rounded-2xl border bg-white p-4 shadow-[var(--elev-1)]">
      <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl" style={{ background: `color-mix(in srgb, ${color} 16%, white)` }}>
        <Icon className="h-5 w-5" style={{ color }} />
      </div>
      <div className="min-w-0">
        <p className="text-2xl font-bold" style={{ color: "#384872", fontFamily: "Sora, sans-serif" }}>{value}</p>
        <p className="text-xs text-muted-foreground">{label}</p>
      </div>
    </div>
  );
}

interface Props {
  title: string;
  subtitle?: string;
  // Couleur de domaine (onglet actif + icône-pastille de l'en-tête).
  accent?: string;
  // Icône de domaine dans l'en-tête (emoji ou node).
  headerIcon?: ReactNode;
  // Onglets secondaires optionnels affichés sous le titre (ex. Préparation / Débrief).
  tabs?: ReactNode;
  sections: WorkspaceSection[];
  active: string;
  onChange: (id: string) => void;
  children: ReactNode;
}

export function SessionsWorkspace({ title, subtitle, accent = "#384872", headerIcon, tabs, sections, active, onChange, children }: Props) {
  return (
    <div className="mx-auto max-w-5xl px-4 py-6">
      <div className={`flex items-center gap-3 ${tabs ? "mb-3" : "mb-4"}`}>
        {headerIcon && (
          <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl text-lg text-white" style={{ background: accent }}>{headerIcon}</span>
        )}
        <div className="min-w-0">
          <h1 className="text-2xl md:text-3xl font-bold" style={{ color: "#384872", fontFamily: "Sora, sans-serif" }}>{title}</h1>
          {subtitle && <p className="text-sm text-muted-foreground">{subtitle}</p>}
        </div>
      </div>

      {tabs && <div className="mb-3 flex gap-2 border-b">{tabs}</div>}

      {/* Onglets de sections — barre horizontale sticky, scrollable sur mobile */}
      <div className="sticky top-16 z-30 mb-5 flex gap-1 overflow-x-auto border-b bg-background/95 backdrop-blur scrollbar-hide" style={{ borderColor: "#E6E4EE" }}>
        {sections.map((s) => {
          const on = active === s.id;
          return (
            <button
              key={s.id}
              data-tour={`ws-section-${s.id}`}
              onClick={() => onChange(s.id)}
              className="flex items-center gap-1.5 whitespace-nowrap px-3.5 py-2.5 text-sm font-semibold transition-colors"
              style={on ? { color: accent, borderBottom: `2px solid ${accent}` } : { color: "#6E6A82" }}
            >
              <s.icon className="h-4 w-4" />{s.label}
            </button>
          );
        })}
      </div>

      {/* Contenu de la section active */}
      <div className="min-w-0">{children}</div>
    </div>
  );
}
